import { useState, useEffect, useRef } from "react";
import { resetPasswordUrl } from "../api/endpoints";
import { uiAlert } from "../components/alerts/customAlert/uiAlert";

/* ─────────────────────────────────────────────────────────────────────
   ForgotPasswordOverlay
   Props:
     onClose  — () => void  — called when overlay should unmount
   Usage:
     import ForgotPasswordOverlay from "./ForgotPasswordOverlay";
     {isOpen && <ForgotPasswordOverlay onClose={() => setIsOpen(false)} />}
───────────────────────────────────────────────────────────────────── */

/* ── Scoped styles (prefix: fp__) ─────────────────────────────────── */

const FP_STYLES = `


  /* ── Keyframes ── */
  @keyframes fp__fadeIn        { from { opacity:0 }          to { opacity:1 } }
  @keyframes fp__cardSlideIn   { from { opacity:0; transform:translateY(38px) scale(.955) } to { opacity:1; transform:translateY(0) scale(1) } }
  @keyframes fp__cardSlideOut  { from { opacity:1; transform:translateY(0) scale(1) }       to { opacity:0; transform:translateY(38px) scale(.955) } }
  @keyframes fp__stepReveal    { from { opacity:0; transform:translateY(13px) }              to { opacity:1; transform:translateY(0) } }
  @keyframes fp__envPop        { from { transform:scale(.45) rotate(-10deg); opacity:0 }    to { transform:scale(1) rotate(0deg); opacity:1 } }
  @keyframes fp__checkDraw     { to   { stroke-dashoffset:0 } }
  @keyframes fp__inputShake    { 0%,100%{transform:translateX(0)} 20%{transform:translateX(-7px)} 40%{transform:translateX(7px)} 60%{transform:translateX(-4px)} 80%{transform:translateX(4px)} }
  @keyframes fp__spin          { to { transform:rotate(360deg) } }

  /* ── Overlay backdrop ── */
  .fp__overlay {
    position: fixed;
    inset: 0;
    z-index: 999;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    animation: fp__fadeIn .28s ease both;
  }

  .fp__backdrop {
    position: absolute;
    inset: 0;
    background: rgba(8, 4, 0, 0.64);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    cursor: pointer;
  }

  /* ── Modal card ── */
  .fp__card {
    position: relative;
    z-index: 2;
    background: rgba(255, 255, 255, 0.96);
    backdrop-filter: blur(24px);
    -webkit-backdrop-filter: blur(24px);
    border: 1px solid rgba(255, 255, 255, 0.82);
    border-radius: 28px;
    padding: 52px 42px 44px;
    width: min(432px, 100%);
    box-shadow: 0 32px 80px rgba(0, 0, 0, 0.28), 0 0 0 1px rgba(59,111,245,0.04);
  }
  .fp__card--entering { animation: fp__cardSlideIn  .44s cubic-bezier(.22,1,.36,1) both; }
  .fp__card--exiting  { animation: fp__cardSlideOut .32s ease both; }

  /* ── Close button ── */
  .fp__close-btn {
    position: absolute;
    top: 16px;
    right: 16px;
    width: 36px;
    height: 36px;
    border: none;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.07);
    color: #6b7280;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background .2s, color .2s;
    flex-shrink: 0;
  }
  .fp__close-btn:hover { background: rgba(0,0,0,.13); color: #1a1f36; }

  /* ── Step wrapper ── */
  .fp__step {
    animation: fp__stepReveal .38s cubic-bezier(.22,1,.36,1) both;
  }

  /* ── Icon box ── */
  .fp__icon-box {
    width: 68px;
    height: 68px;
    border-radius: 20px;
    background: linear-gradient(135deg, rgba(59,111,245,.11), rgba(59,111,245,.05));
    border: 2px solid rgba(59,111,245,.18);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 22px;
    color: #3b6ff5;
  }

  /* ── Typography ── */
  .fp__title {
    
    font-size: 22px;
    font-weight: 700;
    color: #1a1f36;
    text-align: center;
    margin-bottom: 10px;
  }

  .fp__subtitle {
    
    font-size: 14.5px;
    color: #6b7280;
    text-align: center;
    line-height: 1.68;
    margin-bottom: 28px;
  }

  .fp__subtitle strong { color: #1a1f36; font-weight: 500; }

  /* ── Field ── */
  .fp__field { margin-bottom: 6px; }

  .fp__field-label {
    display: block;
    
    font-size: 13px;
    font-weight: 500;
    color: #1a1f36;
    margin-bottom: 7px;
  }

  .fp__input-wrap { position: relative; }

  .fp__input-icon {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: #b8c0cc;
    line-height: 0;
    pointer-events: none;
  }

  .fp__input {
    width: 100%;
    padding: 14px 18px 14px 44px;
    background: #fff;
    border: 1.5px solid rgba(0, 0, 0, 0.09);
    border-radius: 12px;
    
    font-size: 15px;
    color: #1a1f36;
    outline: none;
    transition: border-color .2s, box-shadow .2s;
  }
  .fp__input:focus {
    border-color: #3b6ff5;
    box-shadow: 0 0 0 3.5px rgba(59,111,245,.11);
  }
  .fp__input::placeholder { color: #b0b8c8; }

  .fp__input--error {
    border-color: #ef4444 !important;
    box-shadow: 0 0 0 3px rgba(239,68,68,0.12) !important;
  }
  .fp__input--shake { animation: fp__inputShake .42s ease; }

  /* ── Hint & error ── */
  .fp__field-hint {
   
    font-size: 12.5px;
    color: #a8b0c0;
    line-height: 1.5;
    margin-top: 7px;
    margin-bottom: 20px;
  }

  .fp__error-msg {
    display: flex;
    align-items: center;
    gap: 6px;

    font-size: 13px;
    color: #ef4444;
    margin-bottom: 14px;
    animation: fp__stepReveal .25s ease both;
  }

  /* ── Buttons ── */
  .fp__btn-primary {
    width: 100%;
    padding: 14.5px;
    background: linear-gradient(135deg, #3b6ff5, #2a56d8);
    border: none;
    border-radius: 12px;
    color: #fff;
    
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    box-shadow: 0 4px 18px rgba(59,111,245,.38);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: transform .22s, box-shadow .22s;
  }
  .fp__btn-primary:hover:not(:disabled) {
    transform: translateY(-1.5px);
    box-shadow: 0 8px 26px rgba(59,111,245,.46);
  }
  .fp__btn-primary:disabled { opacity: .62; cursor: not-allowed; transform: none; }

  .fp__btn-ghost {
    width: 100%;
    padding: 12.5px;
    background: transparent;
    border: 1.5px solid rgba(0,0,0,0.09);
    border-radius: 12px;
    color: #6b7280;
    
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    margin-top: 10px;
    transition: border-color .2s, color .2s;
  }
  .fp__btn-ghost:hover { border-color: rgba(0,0,0,.2); color: #1a1f36; }

  .fp__link-btn {
    background: none;
    border: none;
    color: #3b6ff5;
   
    font-size: 13.5px;
    font-weight: 500;
    cursor: pointer;
    padding: 0;
    text-decoration: underline;
    text-underline-offset: 2px;
  }
  .fp__link-btn:hover { opacity: .75; }

  /* ── Spinner ── */
  .fp__spinner {
    width: 17px;
    height: 17px;
    border: 2.5px solid rgba(255,255,255,.3);
    border-top-color: #fff;
    border-radius: 50%;
    animation: fp__spin .7s linear infinite;
    flex-shrink: 0;
  }

  /* ── Envelope (step 2) ── */
  .fp__envelope-wrap {
    display: flex;
    justify-content: center;
    margin-bottom: 24px;
    animation: fp__envPop .55s cubic-bezier(.34,1.56,.64,1) both;
  }

  .fp__check-path {
    stroke-dasharray: 22;
    stroke-dashoffset: 22;
    animation: fp__checkDraw .5s .42s cubic-bezier(.22,1,.36,1) forwards;
  }

  /* ── Info callout ── */
  .fp__info-box {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    background: rgba(59,111,245,.07);
    border: 1px solid rgba(59,111,245,.16);
    border-radius: 12px;
    padding: 13px 15px;
    
    font-size: 13.5px;
    color: #4a5568;
    line-height: 1.55;
    margin-bottom: 24px;
  }
  .fp__info-box svg { flex-shrink: 0; margin-top: 1px; }

  /* ── Retry row ── */
  .fp__retry-row {
    text-align: center;
    
    font-size: 13.5px;
    color: #6b7280;
    margin-top: 14px;
  }

  @media (max-width: 480px) {
    .fp__card { padding: 42px 22px 34px; }
  }
`;

/* ── Sub-components ───────────────────────────────────────────────── */
const MailIcon = ({ size = 30, color = "#3b6ff5" }) => (
  <svg width={size} height={size} fill='none' stroke={color} strokeWidth='1.8' viewBox='0 0 24 24'>
    <rect x='2' y='4' width='20' height='16' rx='3' />
    <path d='m2 7 10 7 10-7' />
  </svg>
);

const InfoIcon = ({ size = 16, color = "#3b6ff5" }) => (
  <svg width={size} height={size} fill='none' stroke={color} strokeWidth='2' viewBox='0 0 24 24' style={{ flexShrink: 0, marginTop: 1 }}>
    <circle cx='12' cy='12' r='10' />
    <path d='M12 16v-4M12 8h.01' />
  </svg>
);

const AlertIcon = ({ size = 14 }) => (
  <svg width={size} height={size} fill='none' stroke='currentColor' strokeWidth='2' viewBox='0 0 24 24'>
    <circle cx='12' cy='12' r='10' />
    <path d='M12 8v4M12 16h.01' />
  </svg>
);

const BackArrowIcon = ({ size = 16 }) => (
  <svg width={size} height={size} fill='none' stroke='currentColor' strokeWidth='2' viewBox='0 0 24 24'>
    <path d='M19 12H5M12 5l-7 7 7 7' />
  </svg>
);

const CloseIcon = ({ size = 15 }) => (
  <svg width={size} height={size} fill='none' stroke='currentColor' strokeWidth='2.5' viewBox='0 0 24 24'>
    <path d='M18 6 6 18M6 6l12 12' />
  </svg>
);

const EnvelopeSentIllustration = () => (
  <div className='fp__envelope-wrap'>
    <svg width='94' height='94' viewBox='0 0 94 94' fill='none'>
      {/* Envelope body */}
      <rect x='8' y='24' width='78' height='54' rx='9' fill='rgba(59,111,245,0.08)' stroke='rgba(59,111,245,0.25)' strokeWidth='2' />
      {/* Flap crease */}
      <path d='M8 34 L47 60 L86 34' stroke='rgba(59,111,245,0.4)' strokeWidth='2' strokeLinejoin='round' />
      {/* Bottom fold lines */}
      <path d='M8 78 L34 54' stroke='rgba(59,111,245,0.18)' strokeWidth='1.5' strokeLinecap='round' />
      <path d='M86 78 L60 54' stroke='rgba(59,111,245,0.18)' strokeWidth='1.5' strokeLinecap='round' />
      {/* Green check badge */}
      <circle cx='71' cy='27' r='18' fill='#22c55e' stroke='white' strokeWidth='2.5' />
      <path className='fp__check-path' d='M63 27 L68.5 32.5 L80 21' stroke='white' strokeWidth='2.8' strokeLinecap='round' strokeLinejoin='round' />
    </svg>
  </div>
);

/* ── Step 1: Email entry ───────────────────────────────────────────── */
const Step1EmailEntry = ({ emailRef, email, onChange, onSend, onBack, loading, error, shaking }) => (
  <div className='fp__step'>
    <div className='fp__icon-box'>
      <MailIcon />
    </div>

    <h3 className='fp__title'>Forgot Password?</h3>
    <p className='fp__subtitle'>Enter your registered email address. If it's in our system, we'll send you a secure reset link right away.</p>

    <div className='fp__field'>
      <label className='fp__field-label' htmlFor='fp-email-input'>
        Email address
      </label>
      <div className='fp__input-wrap'>
        <span className='fp__input-icon'>
          <MailIcon size={17} color='#b8c0cc' />
        </span>
        <input
          ref={emailRef}
          id='fp-email-input'
          type='email'
          placeholder='you@company.com'
          value={email}
          onChange={onChange}
          onKeyDown={(e) => e.key === "Enter" && onSend()}
          className={["fp__input", error ? "fp__input--error" : "", shaking ? "fp__input--shake" : ""].filter(Boolean).join(" ")}
          autoComplete='email'
        />
      </div>
      <p className='fp__field-hint'>For security, we won't confirm whether an account exists.</p>
    </div>

    {error && (
      <div className='fp__error-msg'>
        <AlertIcon />
        Please enter a valid email address.
      </div>
    )}

    <button className='fp__btn-primary' onClick={onSend} disabled={loading}>
      {loading ? (
        <>
          <span className='fp__spinner' />
          Sending…
        </>
      ) : (
        "Send Reset Link"
      )}
    </button>

    <button className='fp__btn-ghost' onClick={onBack}>
      ← Back to Sign In
    </button>
  </div>
);

/* ── Step 2: Confirmation ─────────────────────────────────────────── */
const Step2Confirmation = ({ submittedEmail, onClose, onRetry }) => (
  <div className='fp__step'>
    <EnvelopeSentIllustration />

    <h3 className='fp__title'>Check Your Inbox</h3>
    <p className='fp__subtitle'>
      If <strong>{submittedEmail}</strong> is registered with StoreLense, a password reset link has been sent.
    </p>

    <div className='fp__info-box'>
      <InfoIcon />
      <span>
        Can't find it? Check your <strong>spam or junk folder</strong>. The link expires in <strong>10 minutes</strong>.
      </span>
    </div>

    <button className='fp__btn-primary' onClick={onClose}>
      <BackArrowIcon />
      Back to Sign In
    </button>

    <p className='fp__retry-row'>
      Wrong email?{" "}
      <button className='fp__link-btn' onClick={onRetry}>
        Try a different address
      </button>
    </p>
  </div>
);

/* ── Main export ──────────────────────────────────────────────────── */
export default function ForgotPasswordOverlay({ onClose, showOverlayToResetPassword }) {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [shaking, setShaking] = useState(false);
  const [closing, setClosing] = useState(false);
  const emailRef = useRef(null);

  /* Inject scoped styles once */
  useEffect(() => {
    const id = "fp-overlay-styles";
    if (!document.getElementById(id)) {
      const tag = document.createElement("style");
      tag.id = id;
      tag.textContent = FP_STYLES;
      document.head.appendChild(tag);
    }
    /* Focus email input after card animation */
    const t = setTimeout(() => emailRef.current?.focus(), 440);
    return () => clearTimeout(t);
  }, []);

  const validateEmail = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim());

  const handleClose = () => {
    setClosing(true);
    setTimeout(onClose, 340);
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (error) setError(false);
  };

  const handleSend = async () => {
    if (!validateEmail(email)) {
      setError(true);
      setShaking(true);
      setTimeout(() => setShaking(false), 500);
      return;
    }

    setLoading(true);

    try {
      const resendPasURL = resetPasswordUrl;
      let response = await fetch(resendPasURL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });

      // Simulate API call
      /*  await new Promise((resolve) => setTimeout(resolve, 2000)); */
      const data = await response.json();

      if (response.status === 404) {
        setLoading(false);
        uiAlert(data.message, "error");
        return;
      }

      if (!response.ok) {
        throw new Error("Failed to send reset link. Please try again.");
      }
      /* 
      alert("Reset Link Sent!", data.message || "reset link sent", [
        {
          text: "OK",
          onPress: () => {
            // Optional: Navigate to login after a delay
            setTimeout(() => {
              router.replace("/(auth)/login");
            }, 1000);
          },
        },
      ]); */

      uiAlert(data.message || "Reset link sent! Please check your inbox.", "success");

      // Show success message
      setError(false);

      setTimeout(() => {
        setSubmittedEmail(email.trim());
        setLoading(false);
        setStep(2);
      }, 1700);
    } catch (error) {
      setLoading(false);
      uiAlert(error.message || "An error occurred. Please try again.", "error");
    }
  };

  const handleRetry = () => {
    setStep(1);
    setEmail("");
    setError(false);
    setTimeout(() => emailRef.current?.focus(), 50);
  };

  if (!showOverlayToResetPassword) return null;

  return (
    <div className='fp__overlay'>
      <style>{FP_STYLES}</style>

      {/* Clicking backdrop closes */}
      <div className='fp__backdrop' onClick={handleClose} role='presentation' />

      <div className={`fp__card fp__card--${closing ? "exiting" : "entering"}`}>
        {/* Close × */}
        <button className='fp__close-btn' onClick={handleClose} aria-label='Close forgot password dialog'>
          <CloseIcon />
        </button>

        {step === 1 && (
          <Step1EmailEntry
            emailRef={emailRef}
            email={email}
            onChange={handleEmailChange}
            onSend={handleSend}
            onBack={handleClose}
            loading={loading}
            error={error}
            shaking={shaking}
          />
        )}

        {step === 2 && <Step2Confirmation submittedEmail={submittedEmail} onClose={handleClose} onRetry={handleRetry} />}
      </div>
    </div>
  );
}
